import { IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { SortableItem } from "lib/@components/SortableItem"
import { useState } from "react"
import Iconify from "minimals-template/components/Iconify"
import { visibleColumnCount } from "lib/@components/managed-columns/column-state.mjs"
import { If } from "lib/@components/switch"

export function ManageColumnRow({ item: column }) {
    const { columnState, pinColumn, toggleColumnVisibility, autoHiddenColumns, minVisible = 1 } = useBoundContext()
    const [dragHandleProps, setDragHandleProps] = useState({})
    const isHidden = (columnState?.hidden ?? []).includes(column.field)
    const isPinned = columnState?.pinnedField === column.field
    const lastVisible = !isHidden && visibleColumnCount(columnState) <= minVisible
    const hasNoData = !isPinned && !!autoHiddenColumns?.has(column.field)

    return (
        <SortableItem id={column.field} setDragProps={setDragHandleProps}>
            <Tooltip
                arrow
                title={hasNoData ? "This column is hidden automatically as it contains no data" : ""}
                placement="top"
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ py: 0.5, px: 1 }}
                    data-cy={`manage-column-row-${column.field}`}
                >
                    <Tooltip title={`Drag to reorder ${column.label}`} placement="bottom" arrow>
                        <IconButton
                            size="small"
                            sx={{ cursor: "grab", mr: 0.5, color: "text.disabled" }}
                            {...dragHandleProps}
                        >
                            <Iconify icon="mdi:drag-vertical" width={16} />
                        </IconButton>
                    </Tooltip>

                    <Typography variant="body2" noWrap sx={{ flex: 1, color: hasNoData ? "text.disabled" : undefined }}>
                        {column.label}
                    </Typography>

                    <If value={!hasNoData}>
                        <Stack direction="row">
                            <Tooltip
                                arrow
                                title={isPinned ? `Unpin ${column.label}` : `Pin ${column.label} to the left`}
                                placement="bottom"
                            >
                                <span>
                                    <IconButton
                                        aria-label={
                                            isPinned ? `Unpin ${column.label}` : `Pin ${column.label} to the left`
                                        }
                                        size="small"
                                        data-cy={`col-pin-${column.field}`}
                                        disabled={!column.pinnable}
                                        onClick={handlePin}
                                        sx={{ color: isPinned ? "primary.main" : "action.disabled" }}
                                    >
                                        <Iconify icon={isPinned ? "mdi:pin" : "mdi:pin-outline"} width={16} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip
                                arrow
                                title={
                                    !column.hideable
                                        ? "Unable to Hide"
                                        : isPinned
                                          ? `Unpin ${column.label} to hide`
                                          : isHidden
                                            ? `Show ${column.label}`
                                            : `Hide ${column.label}`
                                }
                                placement="bottom"
                            >
                                <span>
                                    <IconButton
                                        aria-label={isHidden ? `Show ${column.label}` : `Hide ${column.label}`}
                                        size="small"
                                        data-cy={`col-vis-${column.field}`}
                                        disabled={!column.hideable || lastVisible || isPinned}
                                        onClick={handleToggleVisibility}
                                        sx={{ color: isHidden ? "action.disabled" : "primary.main" }}
                                    >
                                        <Iconify icon={isHidden ? "mdi:eye-off" : "mdi:eye"} width={16} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Stack>
                    </If>
                </Stack>
            </Tooltip>
        </SortableItem>
    )

    function handleToggleVisibility() {
        toggleColumnVisibility(column.field)
    }

    function handlePin() {
        pinColumn(column.field)
    }
}
